import { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { RoundedBox } from '@react-three/drei';
import * as THREE from 'three';

/* ── skeuomorphic material helpers ─────────────────────────── */
const Metal = ({ c = '#8b95a8', r = 0.32, e = null, gi = 0 }) => (
    <meshStandardMaterial color={c} metalness={0.92} roughness={r} envMapIntensity={1.15} emissive={e || '#000000'} emissiveIntensity={gi} />
);

const Clay = ({ c = '#1a2030', r = 0.5 }) => (
    <meshStandardMaterial color={c} metalness={0.1} roughness={r} envMapIntensity={0.6} />
);

const Glass = ({ c = '#0d1526' }) => (
    <meshPhysicalMaterial color={c} metalness={0.55} roughness={0.08} clearcoat={1} clearcoatRoughness={0.08} envMapIntensity={1.3} />
);

const Glow = ({ c, i = 1 }) => (
    <meshStandardMaterial color={c} emissive={c} emissiveIntensity={i} metalness={0.3} roughness={0.25} envMapIntensity={0.8} />
);

const RB = ({ a, p = [0, 0, 0], r = 0.05, rot = null, children }) => (
    <RoundedBox
        args={a}
        position={p}
        radius={r}
        smoothness={3}
        castShadow
        receiveShadow
        {...(rot ? { rotation: rot } : {})}
    >
        {children}
    </RoundedBox>
);

function ActiveGroup({ active, children }) {
    const g = useRef(null);
    const target = new THREE.Vector3(1, 1, 1);
    useFrame((_, dt) => {
        if (!g.current) return;
        const s = active ? 1.07 : 1;
        g.current.scale.lerp(target.set(s, s, s), 1 - Math.pow(0.002, dt));
    });
    return <group ref={g}>{children}</group>;
}

/* 00 — Origin Beacon */
function Beacon({ accent, active }) {
    return (
        <ActiveGroup active={active}>
            <RB a={[1.05, 3.4, 1.05]} p={[0, 1.95, 0]} r={0.07}><Clay c="#232b3d" r={0.42} /></RB>
            <RB a={[1.15, 0.14, 1.15]} p={[0, 3.1, 0]} r={0.04}><Metal c={accent} r={0.25} /></RB>
            <mesh position={[0, 1.9, 0.54]}>
                <boxGeometry args={[0.14, 2.5, 0.03]} />
                <Glow c={accent} i={active ? 1.4 : 0.6} />
            </mesh>
            <RB a={[2.1, 0.3, 2.1]} p={[0, 0.4, 0]} r={0.06}><Clay c="#181e2c" r={0.45} /></RB>
        </ActiveGroup>
    );
}

/* 01 — Academic Courtyard */
function Courtyard({ accent, active }) {
    const cols = [[-1.05, -1.05], [1.05, -1.05], [-1.05, 1.05], [1.05, 1.05]];
    return (
        <ActiveGroup active={active}>
            <RB a={[2.9, 0.35, 2.9]} p={[0, 0.42, 0]} r={0.05}><Clay c="#1b2231" r={0.45} /></RB>
            {cols.map(([x, z], i) => (
                <mesh key={i} position={[x, 1.1, z]} castShadow>
                    <cylinderGeometry args={[0.14, 0.16, 1.15, 16]} />
                    <Metal c="#9aa4b8" r={0.3} />
                </mesh>
            ))}
            <RB a={[2.9, 0.28, 2.9]} p={[0, 1.8, 0]} r={0.06}><Clay c="#20293c" r={0.4} /></RB>
            <RB a={[0.55, 0.85, 0.55]} p={[0, 1.05, 0]} r={0.08}><Glass c="#0c1424" /></RB>
            <mesh position={[0, 1.05, 0]}>
                <boxGeometry args={[0.2, 0.5, 0.2]} />
                <Glow c={accent} i={active ? 1.2 : 0.5} />
            </mesh>
        </ActiveGroup>
    );
}

/* 02 — Tech Workshop */
function Workshop({ accent, active }) {
    return (
        <ActiveGroup active={active}>
            <RB a={[2.2, 1.6, 2.2]} p={[0, 1.05, 0]} r={0.06}><Clay c="#1b2231" r={0.42} /></RB>
            <mesh position={[0, 1.1, 1.12]}>
                <boxGeometry args={[1.5, 0.12, 0.03]} />
                <Glow c={accent} i={active ? 1.3 : 0.55} />
            </mesh>
            <mesh position={[0.7, 2.35, -0.5]} castShadow>
                <cylinderGeometry args={[0.05, 0.05, 1.3, 12]} />
                <Metal c="#a8b2c6" r={0.28} />
            </mesh>
            <mesh position={[0.7, 3.05, -0.5]}>
                <boxGeometry args={[0.16, 0.16, 0.16]} />
                <Glow c={accent} i={active ? 1.6 : 0.7} />
            </mesh>
            <RB a={[0.8, 0.5, 0.8]} p={[-0.6, 2.0, 0.5]} r={0.05}><Glass c="#0d1526" /></RB>
        </ActiveGroup>
    );
}

/* 03 — Divergence Bridge */
function Bridge({ accent, active }) {
    return (
        <ActiveGroup active={active}>
            <mesh position={[-1.15, 0.85, 0]} castShadow>
                <boxGeometry args={[0.3, 1.35, 0.3]} />
                <Metal c="#7d879b" r={0.35} />
            </mesh>
            <mesh position={[1.15, 0.85, 0]} castShadow>
                <boxGeometry args={[0.3, 1.35, 0.3]} />
                <Metal c="#7d879b" r={0.35} />
            </mesh>
            <mesh position={[0, 1.62, 0]} rotation={[0, 0, 0.24]} castShadow>
                <boxGeometry args={[3.3, 0.14, 0.72]} />
                <Clay c="#232b3d" r={0.38} />
            </mesh>
            <mesh position={[0, 1.4, 0]} rotation={[0, 0, 0.24]}>
                <boxGeometry args={[3.1, 0.05, 0.08]} />
                <Glow c={accent} i={active ? 1.5 : 0.6} />
            </mesh>
        </ActiveGroup>
    );
}

/* 04 — Cloud Server Tower */
function ServerTower({ accent, active }) {
    const rows = [1.35, 1.75, 2.15];
    return (
        <ActiveGroup active={active}>
            {rows.map((y, i) => (
                <RB key={i} a={[1.9 - i * 0.22, 0.42, 1.9 - i * 0.22]} p={[0, y, 0]} r={0.04}>
                    <Metal c="#39435a" r={0.42} />
                </RB>
            ))}
            {rows.map((y, i) => (
                <mesh key={`s${i}`} position={[0.62, y, 0.96 - i * 0.11]}>
                    <boxGeometry args={[0.5, 0.06, 0.02]} />
                    <Glow c={accent} i={active ? 1.4 : 0.55} />
                </mesh>
            ))}
        </ActiveGroup>
    );
}

/* 05 — Corporate Matrix */
function Matrix({ accent, active }) {
    const wins = [];
    for (let r = 0; r < 3; r++) for (let c = 0; c < 4; c++) wins.push([r, c]);
    return (
        <ActiveGroup active={active}>
            <RB a={[2.6, 2.0, 2.3]} p={[0, 1.25, 0]} r={0.05}><Clay c="#1b2231" r={0.4} /></RB>
            <RB a={[2.75, 0.16, 2.45]} p={[0, 2.36, 0]} r={0.04}><Metal c="#8b95a8" r={0.3} /></RB>
            {wins.map(([r, c], i) => (
                <mesh key={i} position={[-0.9 + c * 0.6, 0.75 + r * 0.55, 1.17]}>
                    <boxGeometry args={[0.34, 0.3, 0.02]} />
                    <Glass c="#0a1120" />
                </mesh>
            ))}
            {wins.filter(([, c]) => c === 1 || c === 2).map(([r, c], i) => (
                <mesh key={`g${i}`} position={[-0.9 + c * 0.6, 0.75 + r * 0.55, 1.18]}>
                    <boxGeometry args={[0.2, 0.16, 0.01]} />
                    <Glow c={accent} i={(r + c) % 2 === 0 || active ? (active ? 1.3 : 0.7) : 0.3} />
                </mesh>
            ))}
        </ActiveGroup>
    );
}

/* 06 — On-Chain Vault */
function Vault({ accent, active }) {
    const cube = useRef(null);
    const ring = useRef(null);
    useFrame((_, dt) => {
        if (cube.current) cube.current.rotation.y += dt * (active ? 0.9 : 0.25);
        if (ring.current) {
            ring.current.rotation.z += dt * 0.35;
            ring.current.rotation.x = Math.PI / 2.4;
        }
    });
    return (
        <ActiveGroup active={active}>
            <mesh position={[0, 0.35, 0]} castShadow>
                <cylinderGeometry args={[1.25, 1.45, 0.5, 24]} />
                <Metal c="#4a5468" r={0.3} />
            </mesh>
            <group ref={cube} position={[0, 1.55, 0]}>
                <mesh castShadow>
                    <boxGeometry args={[1.35, 1.35, 1.35]} />
                    <Glass c="#141d33" />
                </mesh>
                <lineSegments>
                    <edgesGeometry args={[new THREE.BoxGeometry(1.36, 1.36, 1.36)]} />
                    <lineBasicMaterial color={accent} transparent opacity={active ? 1 : 0.5} />
                </lineSegments>
            </group>
            <group ref={ring} position={[0, 1.55, 0]}>
                <mesh castShadow>
                    <torusGeometry args={[1.12, 0.06, 16, 40]} />
                    <Metal c={accent} r={0.22} />
                </mesh>
            </group>
        </ActiveGroup>
    );
}

/* 07 — Parallel Annex */
function Annex({ accent, active }) {
    return (
        <ActiveGroup active={active}>
            <RB a={[0.9, 1.6, 0.9]} p={[-0.55, 1.0, 0]} r={0.06}><Clay c="#1e2637" r={0.42} /></RB>
            <RB a={[0.7, 0.9, 0.7]} p={[0.75, 0.65, 0]} r={0.05}><Clay c="#232b3d" r={0.45} /></RB>
            <mesh position={[0.1, 1.35, 0]}>
                <boxGeometry args={[1.7, 0.08, 0.14]} />
                <Metal c={accent} r={0.25} />
            </mesh>
            <mesh position={[-0.55, 1.95, 0]}>
                <boxGeometry args={[0.18, 0.18, 0.18]} />
                <Glow c={accent} i={active ? 1.5 : 0.6} />
            </mesh>
        </ActiveGroup>
    );
}

/* 08 — THE PEAK CITADEL */
function Citadel({ accent, active }) {
    return (
        <ActiveGroup active={active}>
            <RB a={[2.3, 1.0, 2.3]} p={[0, 0.75, 0]} r={0.06}><Clay c="#2a2438" r={0.42} /></RB>
            <RB a={[1.7, 1.5, 1.7]} p={[0, 2.0, 0]} r={0.05}><Clay c="#322c40" r={0.4} /></RB>
            <RB a={[1.15, 1.3, 1.15]} p={[0, 3.4, 0]} r={0.05}><Glass c="#1a1626" /></RB>
            <mesh position={[0, 4.35, 0]}>
                <boxGeometry args={[0.3, 0.55, 0.3]} />
                <Glow c={accent} i={active ? 2.2 : 1.1} />
            </mesh>
            {[[-0.62, -0.62], [0.62, 0.62]].map(([x, z], i) => (
                <mesh key={i} position={[x, 4.6, z]} rotation={[0, 0, i ? -0.18 : 0.18]} castShadow>
                    <boxGeometry args={[0.07, 1.5, 0.07]} />
                    <Metal c="#5a5040" r={0.35} />
                </mesh>
            ))}
            <mesh position={[0.62, 5.25, 0.62]} rotation={[0, 0, -0.5]}>
                <boxGeometry args={[0.85, 0.05, 0.05]} />
                <Metal c={accent} r={0.25} />
            </mesh>
            <pointLight position={[0, 4.7, 0]} color={accent} intensity={active ? 26 : 12} distance={10} decay={2} />
        </ActiveGroup>
    );
}

const BUILDINGS = [Beacon, Courtyard, Workshop, ServerTower, Bridge, Matrix, Vault, Annex, Citadel];

export function ChapterNode({ index, accent, active, position, rotation = 0 }) {
    const Building = BUILDINGS[index % BUILDINGS.length];
    return (
        <group position={position} rotation-y={rotation}>
            <RoundedBox args={[3.6, 0.5, 3.6]} radius={0.1} smoothness={3} position={[0, 0, 0]} castShadow receiveShadow>
                <meshStandardMaterial color="#1a2234" metalness={0.6} roughness={0.22} envMapIntensity={1.1} />
            </RoundedBox>
            <group position={[0, 0.25, 0]}>
                <Building accent={accent} active={active} />
            </group>
        </group>
    );
}
